import { API_HOST, authenticatedFetchData } from '../../../lib'
import { logError } from '@tomra/datadog-browser-logging'
import type React from 'react'
import { useState } from 'react'
import { isBefore, isAfter, isValid, differenceInDays, addMonths, endOfMonth } from 'date-fns'
import { Dialog } from '../../common/Dialog'
import { Button } from '@tomra/design-system-react/button'

interface BagdropReportButtonProps {
  fromDate: Date
  toDate: Date
  appContext: CharityAppContext
  state: string
  onFromDateChange: (date: Date) => void
  onToDateChange: (date: Date) => void
  onAppContextChange: (appContext: CharityAppContext) => void
  onStateChange: (state: string) => void
  recordCount: number | null
}

export const BagdropReportButton: React.FC<BagdropReportButtonProps> = ({
  fromDate,
  toDate,
  appContext,
  state,
  onFromDateChange,
  onToDateChange,
  onAppContextChange,
  onStateChange,
  recordCount
}) => {
  const formatDateToString = (date: Date) => {
    return date.toISOString().substring(0, 10)
  }

  const [status, setStatus] = useState<RequestStatusType>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const [showDialog, setShowDialog] = useState(false)
  const appContexts: CharityAppContext[] = ['RETURNANDEARN', 'CDSVICWEST', 'RECYCLEREWARDS']
  const states = ['UNLINKED', 'PROCESSED', 'PENDING']

  const createBlob = (data: string) => new Blob([data], { type: 'text/csv;charset=utf-8;' })

  const triggerDownload = (blob: Blob) => {
    try {
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', `bagdrop_report.csv`)
        link.style.visibility = 'hidden'
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (_error) {
      throw new Error(`Error while triggering bagdrop report download`)
    }
  }

  const validateAndUpdateDates = (from: Date, to: Date) => {
    let adjustedTo = to
    const isToChanged = to.getDate() !== toDate.getDate()

    if (!isValid(from)) {
      setErrorMsg(`The selected "From" date (${formatDateToString(from)}) is invalid`)
    } else if (!isValid(to)) {
      setErrorMsg(`The selected "To" date (${formatDateToString(to)}) is invalid`)
    } else {
      const daysDiff = differenceInDays(to, from)

      if ((daysDiff > 30 || daysDiff < 1) && !isToChanged) {
        if (from.getDate() === 1) {
          adjustedTo = endOfMonth(from)
        } else {
          adjustedTo = addMonths(from, 1)
        }
        setErrorMsg('')
      } else {
        setErrorMsg('')
      }
    }

    onFromDateChange(from)
    onToDateChange(adjustedTo)
  }

  const getReportData = () => {
    setStatus('pending')
    setShowDialog(false)

    const todate = toDate.toISOString().substring(0, 10) + 'T23:59:59.99Z'
    const fromdate = fromDate.toISOString().substring(0, 10) + 'T00:00:00.00Z'
    authenticatedFetchData(
      `${API_HOST}/mytomra/v1.0/hotlinetool/bags/${state.toLowerCase()}/report?appContext=${appContext}&from=${fromdate}&to=${todate}`
    )
      .run()
      .then(createBlob)
      .then(triggerDownload)
      .then(() => setStatus('idle'))
      .catch((error: any) => {
        setStatus('failure')
        setErrorMsg('An error occurred while processing report data')
        logError(new Error(`Unable to download bagdrop report`), error)
      })
  }

  return (
    <>
      <div className="p-6 flex items-end gap-1 flex-wrap">
        <div>
          <label htmlFor="fromDate" className="block mb-1">
            From
          </label>

          <input
            id="fromDate"
            value={formatDateToString(fromDate)}
            type="date"
            required
            onChange={({ target }) => validateAndUpdateDates(new Date(target.value), toDate)}
            className="border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="toDate" className="block mb-1">
            To
          </label>

          <input
            id="toDate"
            value={formatDateToString(toDate)}
            type="date"
            required
            onChange={({ target }) => validateAndUpdateDates(fromDate, new Date(target.value))}
            className="border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="appContext" className="block mb-1">
            App
          </label>

          <select
            id="appContext"
            value={appContext}
            onChange={({ target }) => onAppContextChange(target.value as CharityAppContext)}
            className="border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            {appContexts.map((ac, key) => (
              <option key={`btn-${key}`} value={ac}>
                {ac}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor="state" className="block mb-1">
            State
          </label>

          <select
            id="state"
            value={state}
            onChange={({ target }) => onStateChange(target.value)}
            className="border border-gray-300 rounded px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
          >
            {states.map((s, key) => (
              <option key={`state-${key}`} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-1 justify-end">
          <Button
            variant="outline"
            onClick={() => setShowDialog(true)}
            disabled={status === 'pending' || recordCount === 0 || recordCount === null}
            aria-disabled={status === 'pending'}
          >
            {status === 'pending' ? <span data-testid="spinner" className="loadingSpinner" /> : 'Export'}
          </Button>
        </div>
      </div>
      {errorMsg && (
        <div className="text-rose-700 text-sm p-6 justify-end flex">
          <div>{errorMsg}</div>
        </div>
      )}

      {showDialog && (
        <Dialog ariaLabel="Confirm Export" onClose={() => setShowDialog(false)}>
          <div className="rounded-2xl bg-white shadow-md dark:bg-gray-900 min-w-[768px] max-w-[768px] max-h-[90vh] overflow-hidden p-6">
            <h2 className="text-xl font-semibold mb-4">Confirm Export</h2>
            <p className="mb-6">
              Are you sure you want to export? This will export all bags with the selected parameters:
            </p>
            <ul className="list-disc list-inside mb-6">
              <li>
                <strong className="inline-block w-24">From:</strong> {formatDateToString(fromDate)}
              </li>
              <li>
                <strong className="inline-block w-24">To:</strong> {formatDateToString(toDate)}
              </li>
              <li>
                <strong className="inline-block w-24">App:</strong> {appContext}
              </li>
              <li>
                <strong className="inline-block w-24">State:</strong> {state}
              </li>
            </ul>
            <p className="mb-6">The result will be a CSV file. This might take some time.</p>

            <div className="rounded-lg border-l-[3px] border-amber-400 bg-white p-4 text-left shadow-md dark:bg-gray-900 mb-4">
              <b>NB!</b> Pending and processed bags will only export bags that are linked to a user, not a charity.
            </div>

            <div className="flex gap-4 justify-end">
              <Button onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button variant="primary" onClick={getReportData}>
                Confirm
              </Button>
            </div>
          </div>
        </Dialog>
      )}
    </>
  )
}
